import { PricePlanTypeEnum, SubscriptionIntervalEnum } from '@systemeio/types'
import { CloseIcon, SaveIcon } from '@systemeio/ui/icons'
import {
  CurrencyEnum,
  FormInput,
  FormInputNumber,
  FormSelect,
  FormSelectInline,
  Hint,
  PrimaryButton,
  RadioGroup,
  RadioGroupDataInterface,
  toast,
} from '@systemeio/ui'
import { BadRequest, InternalError } from '@systemeio/util'
import {
  PricePlanCreateErrorsInterface,
  PricePlanCreateInterface,
} from 'modules/digital-product/price-plans/types/price-plan-interface'
import {
  getPricePlansTypesData,
  getSubscriptionIntervalData,
  isLimitedSubscription,
} from 'modules/digital-product/price-plans/utils/price-plans-utils'
import { TaxBehaviorEnum, taxBehaviorLocoKeys } from 'modules/payment/tax-behavior'
import React, { useState } from 'react'
import ButtonLikeLink from 'shared/components/button-like-link'
import { FieldErrorAndDescription } from 'shared/components/form/field-error-and-description'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'

interface CreatePricePlanProps {
  onCreatePricePlan: (value: PricePlanCreateInterface) => Promise<void>
  openAddForm: () => void
  closeForm: () => void
  createErrors: PricePlanCreateErrorsInterface
  currency: CurrencyEnum
}

const defaultValues: PricePlanCreateInterface = {
  name: '',
  innerName: '',
  statementDescriptor: '',
  statementDescriptorKatakana: undefined,
  type: PricePlanTypeEnum.one_shot,
  amount: 0,
  taxBehavior: TaxBehaviorEnum.Exclusive,
  recurringOptions: {
    interval: SubscriptionIntervalEnum.day,
    intervalCount: 0,
    trialPeriod: 0,
    trialInterval: SubscriptionIntervalEnum.day,
    limitOfPayments: undefined,
  },
}

function CreatePricePlan({
  onCreatePricePlan,
  openAddForm,
  closeForm,
  createErrors,
  currency,
}: CreatePricePlanProps) {
  const { t } = useLocoTranslation()
  const [tempState, setTempState] = useState(defaultValues)
  const [isFetching, setIsFetching] = useState(false)
  const [error, setError] = useState('')

  const handleConfirm = async () => {
    try {
      const { recurringOptions, ...pricePlanBaseData } = tempState
      pricePlanBaseData.amount = Math.round(pricePlanBaseData.amount * 100)
      const isOneShot = pricePlanBaseData.type === PricePlanTypeEnum.one_shot
      setError('')
      setIsFetching(true)
      await onCreatePricePlan(
        isOneShot ? pricePlanBaseData : { ...pricePlanBaseData, recurringOptions },
      )
      setIsFetching(false)
      closeForm()
    } catch (e) {
      setIsFetching(false)
      if (e instanceof BadRequest) {
        if (e.errors.common) {
          setError(e.errors.common.join(''))
        }
      } else if (e instanceof InternalError) {
        setError(t('core.error.internal_error_message'))
      } else {
        toast.error(t('global.error'))
      }
    }
  }

  const taxBehaviorOptions: RadioGroupDataInterface<TaxBehaviorEnum>[] = [
    {
      id: TaxBehaviorEnum.Exclusive,
      caption: t(taxBehaviorLocoKeys[TaxBehaviorEnum.Exclusive]),
    },
    {
      id: TaxBehaviorEnum.Inclusive,
      caption: t(taxBehaviorLocoKeys[TaxBehaviorEnum.Inclusive]),
    },
  ]

  return (
    <div className="border-text-secondary/30 relative flex flex-col gap-5 rounded-lg border px-5 py-3">
      <CloseIcon
        className={`cursor-pointer ${
          isFetching && 'fill-text-secondary pointer-events-none'
        } absolute right-1 top-1 lg:right-2 lg:top-2`}
        onClick={() => closeForm()}
      />
      <div
        className={
          '-mb-4 grid grid-cols-1 gap-x-5 gap-y-2 [grid-template-rows:auto_auto_auto] lg:grid-cols-2'
        }
      >
        <FormInput
          label={t('dashboard.funnel.configuration.common.price_plan.name_label')}
          placeholder={t('global.name')}
          value={tempState.name}
          onChange={e =>
            setTempState(prev => {
              return { ...prev, name: e.target.value }
            })
          }
          maxLength={256}
          error={createErrors.name}
          className={'row-span-3 grid w-full gap-1 [grid-template-rows:subgrid]'}
          labelContentWrapperClassName={'row-start-2'}
          errorAndDescriptionClassName={'row-start-3'}
        />
        <FormInput
          label={t('dashboard.funnel.configuration.common.price_plan.inner_name_label')}
          placeholder={t('global.name')}
          value={tempState.innerName}
          onChange={e =>
            setTempState(prev => {
              return { ...prev, innerName: e.target.value }
            })
          }
          maxLength={256}
          error={createErrors.innerName}
          className={'row-span-3 grid w-full gap-1 [grid-template-rows:subgrid]'}
          labelContentWrapperClassName={'row-start-2'}
          errorAndDescriptionClassName={'row-start-3'}
        />
      </div>
      <FormInput
        label={
          <Hint
            label={t(
              'dashboard.funnel.configuration.common.price_plan.statement_descriptor_popover',
            )}
          >
            {t('dashboard.funnel.configuration.common.price_plan.statement_descriptor')}
          </Hint>
        }
        placeholder={t('dashboard.funnel.configuration.common.price_plan.statement_descriptor')}
        value={tempState.statementDescriptor}
        onChange={e =>
          setTempState(prev => {
            return { ...prev, statementDescriptor: e.target.value }
          })
        }
        maxLength={22}
        error={createErrors.statementDescriptor}
      />
      {currency === CurrencyEnum.JAPANESE_YEN && (
        <FormInput
          label={
            <Hint
              label={t(
                'dashboard.funnel.configuration.common.price_plan.statement_descriptor_katakana_popover',
              )}
            >
              {t('dashboard.funnel.configuration.common.price_plan.statement_descriptor_katakana')}
            </Hint>
          }
          placeholder={t(
            'dashboard.funnel.configuration.common.price_plan.statement_descriptor_katakana',
          )}
          value={tempState.statementDescriptorKatakana}
          onChange={e =>
            setTempState(prev => {
              return { ...prev, statementDescriptorKatakana: e.target.value }
            })
          }
          maxLength={22}
          error={createErrors.statementDescriptorKatakana}
        />
      )}
      <FormSelect
        withoutCloseIcon
        label={t('dashboard.funnel.configuration.common.price_plan.type')}
        data={getPricePlansTypesData(t)}
        onChange={data =>
          data &&
          setTempState(prev => {
            return { ...prev, type: data }
          })
        }
        value={tempState.type}
        error={createErrors.type}
      />
      {tempState.type === PricePlanTypeEnum.one_shot && (
        <FormInputNumber
          label={t('dashboard.funnel.configuration.common.price_plan.direct_charge_amount')}
          className={'max-w-[200px]'}
          onChange={value =>
            setTempState(prev => {
              return {
                ...prev,
                amount: value,
              }
            })
          }
          addonAfter={currency}
          value={tempState.amount}
          error={createErrors.amount}
        />
      )}
      {(tempState.type === PricePlanTypeEnum.limited_subscription ||
        tempState.type === PricePlanTypeEnum.subscription) && (
        <>
          <div className="flex items-end gap-5">
            <FormInputNumber
              className={'max-w-[100px]'}
              type={'integer'}
              label={t(
                'dashboard.funnel.configuration.common.price_plan.subscription_plan.trial_period',
              )}
              onChange={value =>
                setTempState(prev => {
                  return {
                    ...prev,
                    recurringOptions: {
                      ...prev.recurringOptions,
                      trialPeriod: value,
                    },
                  }
                })
              }
              value={tempState.recurringOptions?.trialPeriod ?? 0}
              maxLength={256}
              error={createErrors.recurringOptions.trialPeriod}
            />
            <FormSelectInline
              fieldClassName={'mb-[12px]'}
              onChange={interval =>
                setTempState(prev => {
                  return {
                    ...prev,
                    recurringOptions: {
                      ...prev.recurringOptions,
                      trialInterval: interval,
                    },
                  }
                })
              }
              withoutCloseIcon
              value={tempState.recurringOptions?.trialInterval}
              data={getSubscriptionIntervalData(t)}
            />
          </div>
          <div className="flex flex-row flex-wrap items-end gap-5">
            <FormInputNumber
              label={t(
                'dashboard.funnel.configuration.common.price_plan.subscription_plan.customers_will_pay',
              )}
              className={'max-w-[200px]'}
              onChange={value =>
                setTempState(prev => {
                  return {
                    ...prev,
                    amount: value,
                  }
                })
              }
              addonAfter={currency}
              value={tempState.amount}
              error={createErrors.amount}
            />
            <span className="mb-[12px]">{t('global.each')}</span>
            <FormInputNumber
              className={'max-w-[100px]'}
              type={'integer'}
              onChange={value =>
                setTempState(prev => {
                  return {
                    ...prev,
                    recurringOptions: {
                      ...prev.recurringOptions,
                      intervalCount: value,
                    },
                  }
                })
              }
              value={tempState.recurringOptions?.intervalCount ?? 0}
              maxLength={256}
              error={createErrors.recurringOptions.intervalCount}
            />
            <FormSelectInline
              fieldClassName={'mb-[12px]'}
              withoutCloseIcon
              onChange={interval =>
                setTempState(prev => {
                  return {
                    ...prev,
                    recurringOptions: {
                      ...prev.recurringOptions,
                      interval: interval,
                    },
                  }
                })
              }
              value={tempState.recurringOptions?.interval}
              data={getSubscriptionIntervalData(t)}
            />
          </div>
        </>
      )}
      {isLimitedSubscription(tempState.type as PricePlanTypeEnum) && (
        <FormInputNumber
          key="price-plan-limit-of-payments"
          className="max-w-[150px]"
          label={t(
            'dashboard.funnel.configuration.common.price_plan.subscription_plan.limit_of_payments',
          )}
          type={'integer'}
          onChange={value =>
            setTempState(prev => {
              return {
                ...prev,
                recurringOptions: {
                  ...prev.recurringOptions,
                  limitOfPayments: value,
                },
              }
            })
          }
          value={tempState.recurringOptions?.limitOfPayments ?? 0}
          maxLength={256}
          error={createErrors.recurringOptions.limitOfPayments}
        />
      )}
      <RadioGroup
        options={taxBehaviorOptions}
        selected={tempState.taxBehavior}
        onChange={value => setTempState(prev => ({ ...prev, taxBehavior: value }))}
        label={t('dashboard.payment.tax_behavior.title')}
        error={createErrors.taxBehavior}
      />
      <div
        className={
          'text-text-primary flex w-full flex-col gap-2 text-sm font-medium lg:flex-row lg:text-[15px]'
        }
      >
        <span>
          {t('dashboard.funnel.configuration.common.price_plan.create_price_plan_footer')}
        </span>
        <ButtonLikeLink
          onClick={() => {
            openAddForm()
          }}
        >
          {t('dashboard.funnel.configuration.common.price_plan.create_price_plan_footer_link')}
        </ButtonLikeLink>
      </div>
      <div className="flex ">
        <PrimaryButton
          width="large"
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault()
            handleConfirm()
          }}
          isFetching={isFetching}
          type="submit"
        >
          <SaveIcon />
          {t('global.save')}
        </PrimaryButton>
      </div>
      <FieldErrorAndDescription error={error} errorClassName={'text-center mt-2'} />
    </div>
  )
}

export default CreatePricePlan
